import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import { colors } from '@anyroad/theme';
import { capitalize } from 'lib/string_utils';

const GuestNameContainer = styled.div`
  margin-bottom: 16px;
  background-color: ${colors.white};
`;

const GuestLabelRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const GuestLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text.primary};
`;

const TicketLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${colors.text.secondary};
  margin-left: 4px;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 12px;
  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${colors.border};
  font-size: 16px;
  color: ${colors.text.primary};
  background-color: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }
  &::placeholder {
    color: ${colors.text.secondary};
  }
  &:hover:not(:focus) {
    border-color: ${colors.border};
  }
`;

interface GuestNameInputProps {
  index: number;
  selectedTicket: string;
  onChange: (
    index: number,
    value: string,
    fieldName: string,
    unitTypeId: number
  ) => void;
  unitTypeId: number;
  unitTypeName: string;
  hasMultipleUnits: boolean;
  dataTestId?: string;
  firstName?: string;
  lastName?: string;
}

export const GuestNameInput = ({
  index,
  selectedTicket,
  onChange,
  unitTypeId,
  unitTypeName,
  hasMultipleUnits,
  dataTestId,
  firstName: propFirstName = '',
  lastName: propLastName = '',
}: GuestNameInputProps) => {
  const intl = useIntl();
  const [{ firstName, lastName }, setState] = useState({
    firstName: propFirstName,
    lastName: propLastName,
  });

  const handleChange = (fieldName: string, value: string) => {
    setState((prevState) => ({ ...prevState, [fieldName]: value }));

    // Persist additional guest names to legacy state system
    const nameField = fieldName === 'firstName' ? 'first_name' : 'last_name';
    onChange(index, value, nameField, unitTypeId);
  };

  return (
    <GuestNameContainer data-testid={dataTestId}>
      <GuestLabelRow>
        <GuestLabel data-testid="guest-label">
          <FormattedMessage
            defaultMessage="Guest {number}"
            description="Label for identifying each guest by number (e.g., Guest 2, Guest 3)"
            values={{ number: index + 2 }}
          />
        </GuestLabel>
        {hasMultipleUnits && (
          <TicketLabel data-testid="ticket-label">
            {!!selectedTicket && `(${capitalize(unitTypeName)})`}
          </TicketLabel>
        )}
      </GuestLabelRow>
      <InputGroup>
        <InputWrapper>
          <StyledInput
            value={firstName}
            name="firstName"
            placeholder={intl.formatMessage({
              defaultMessage: 'First Name',
              description: 'Placeholder text for first name input field',
            })}
            maxLength={150}
            onChange={(e) => handleChange('firstName', e.target.value)}
          />
        </InputWrapper>
        <InputWrapper>
          <StyledInput
            value={lastName}
            name="lastName"
            placeholder={intl.formatMessage({
              defaultMessage: 'Last Name',
              description: 'Placeholder text for last name input field',
            })}
            maxLength={150}
            onChange={(e) => handleChange('lastName', e.target.value)}
          />
        </InputWrapper>
      </InputGroup>
    </GuestNameContainer>
  );
};
